import React, { useEffect, useState } from 'react';
import useRetrieveData from './useRetrieveData';
import { cleanCopy, guid, parseJson } from '../utils';
import { get, last, orderBy, size, unset, set, first } from '../lib/lodash';
import { useUser } from './useUser';
import { sendData } from '../../redux/actions/sendData';
import { getRowDefs, TransformData } from '../../feature/report/ReportData/DataReports';
import { DateRanges } from '../lib/filter';

export const useChartSettings = props => {
  const { userRealm, realmOrUserRealm } = useUser();

  const [state, setState] = useState({
    charts: [],
    chartsWithData: [],
    loadingChartData: false,
  })

  const chartId = '13';

  const aSettings = useRetrieveData({ url: `/account-settings` }, { build: ['accountSettings'] });

  const data = aSettings.data.find(d => d.id == chartId);

  useEffect(() => {
    if (data) { 
      const charts = orderBy(parseJson(data.value), 'order');
      setState(s => ({ ...s, charts, chartsWithData: charts.map(c => ({ ...formatChartForDisplay(c), loading: true })) }))
    }
  }, [data]);

  const formatChartForDisplay = (chart, d, h) => {
    chart = cleanCopy(chart);
    return {
      ...chart,
      ...get(chart, 'config', {}),
      ...get(chart, 'config.params.meta', {}),
      showSlider: false,
      data: !d ? [] : TransformData(d),
      headerRow: !h ? [] : TransformData(h),
      originalChartData: chart,
    }
  }

  const loadChartData = async (chart, filter, row) => {
    const getSavedChartParams = () => {
      const obj = get(chart, 'config.params.filter', {});
      const daterange = get(chart, 'config.daterange');

      if (daterange) {
        const dr = DateRanges.find(d => d.value == daterange);
        if (!!dr) {
          set(obj, 'start_date', first(dr.vals))
          set(obj, 'end_date', last(dr.vals))
        }
      }

      return obj;
    }

    const removeZeros = (obj) => {
      const newObj = {};
      for (let k in obj) {
        const val = obj[k];
        if (val !== 0 && val !== undefined) {
          newObj[k] = val;
        }
      }
      return newObj;
    }

    //this is because some filters set in the app are defaults and always get sent through
    //however we don't really want them to be
    const defaultIgnoreFilters = [{ key: 'status', value: 'active' }];
    defaultIgnoreFilters.map(f => {
      if (get(filter, f.key) == f.value) {
        unset(filter, f.key);
      }
    })
    
    const params = {
      ...get(chart, 'config.params', {}),
      filter: {
        ...getSavedChartParams(),
        ...removeZeros(filter),
      }
    };

    const def = getRowDefs(row);

    if (row) {
      set(params, 'meta.rows', row);
    }

    const headerparams = cleanCopy(params);
    set(headerparams, 'meta.rows', get(def, 'parent', ''));

    const [res, header] = await Promise.all([
      sendData({ url: '/data-reports' }, {
        useNamespace: false,
        params,
      }),
      sendData({ url: '/data-reports' }, {
        useNamespace: false,
        params: headerparams,
      }),
    ])
    
    const obj = formatChartForDisplay(chart, res.data, header.data);

    unset(obj, 'config');

    return obj;
  }

  const loadCharts = async (filter = {}, filteredCharts) => {
    try {
      let validcharts = filteredCharts ? filteredCharts : state.charts;
      
      setState(s => ({ ...s, chartsWithData: validcharts.map(c => ({ ...formatChartForDisplay(c), loading: true })), loadingChartData: true }))
  
      const results = await Promise.all(validcharts.map(async chart => {
        return await loadChartData(chart, filter);
      })) 
  
      setState(s => ({ ...s, chartsWithData: results }))
    } catch (err) {
      alert(err);
    } finally {
      setState(s => ({ ...s, chartsWithData: s.chartsWithData.map(c => ({ ...c, loading: false })), loadingChartData: false }))
    }
  }

  useEffect(() => {
    const shouldLoadCharts = get(props, 'loadCharts', true);

    if (size(state.charts) && shouldLoadCharts) {
      loadCharts();
    }
  }, [state.charts])

  const updateCharts = async (newcharts) => {
    const id = get(data, 'overrideId');
    const method = id ? 'patch' : 'post';
    return await sendData({
      url: `/realm-account-settings${method === 'patch' ? `/${id}` : ''}`,
      method,
      data: {
        data: {
          type: 'realm-account-settings',
          id: data.override == 1 ? id.toString() : undefined,
          attributes: {
            account_setting_id: chartId,
            realm_id: `${userRealm.id}`,
            value: JSON.stringify(newcharts)
          }
        }
      },
    });
  }

  const deleteChart = async (chart) => {
    const allCharts = state.charts.filter(f => {
      if (f.id == chart.id) {
        return false;
      }
      return true;
    })

    await updateCharts(allCharts);

    await aSettings.update()
  }

  const createAndSave = async vals => {
    try {  
      const chart = vals.chart && vals.chart.id ? vals.chart : {
        name: get(vals, 'name', ''),
        description: get(vals, 'description', ''),
        userGroups: get(vals, 'userGroups', []),
        order: state.charts.length,
        realm_tier: get(realmOrUserRealm, 'tier', ''),
        config: {
          daterange: get(vals, 'chartProps.chartGlobalFilters.daterange'),
          chartIndex: get(vals, 'chartIndex'),
          showCounts: get(vals, 'chartProps.showCounts'),
          useChart: get(vals, 'chartProps.useChart'),
          slide: get(vals, 'chartProps.slide'),
          params: {
            ...get(vals, 'chartProps.opts.params', {}),
            filter: {
              ...get(vals, 'chartProps.opts.params.filter', {}),
              realm_id: undefined,
            }
          },
        }
      }

      let allCharts = [...state.charts];
      if (chart.id) {
        allCharts = allCharts.map(c => {
          if (c.id == chart.id) {
            return chart;
          }
          return c;
        })
      } else{
        allCharts.push({ ...chart, id: guid() });
      }
  
      await updateCharts(allCharts);
      
      await aSettings.update()
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  const filterChartsByUserGroups = (groups, charts) => {
    const flatgroups = groups.map(m => m.id || m);
    return charts.filter(c => {
      const { userGroups } = c;
      return !!userGroups.find(g => flatgroups.includes(g));
    })
  }

  const filterChartsByRealmTier = (realm_tier, charts) => {
    if (!realm_tier) { return charts };

    return charts.filter(c => {
      return c.realm_tier == realm_tier;
    })
  }

  return {
    ...aSettings,
    data,
    ...state,
    createAndSave,
    filterChartsByUserGroups,
    deleteChart,
    updateCharts,
    filterChartsByRealmTier,
    loadCharts,
    loadChartData,
  }
}